/* PlaybookCard.jsx — Operational Playbook · calm premium card.
 *
 * Used by:
 *  • Deal Room Intelligence Rail (renders the active playbook for a deal)
 *  • Automations · Intervention Command Desk (expandable per pending run)
 *
 * Doctrine:
 *  • Calm operational tone — no robotic checklist UI, no AI gimmick.
 *  • Inherits the Director Centre attention palette + premium glass card.
 *  • Every check-tick is an audit-safe write (handled by parent on toggle).
 *  • Auto-degrades gracefully when fields are absent.
 */
import React, { useMemo, useState } from "react";
import {
  Activity, AlertOctagon, ArrowUpRight, BookOpen, CheckCircle2, ChevronDown,
  ChevronUp, ClipboardList, Clock, ExternalLink, Gauge, GitBranch, History,
  Layers3, Send, ShieldCheck, Sparkles, TrendingDown, TrendingUp, Users, X,
} from "lucide-react";
import { attentionToneFor, fmtRelativeTime } from "../director/attentionTone";

/* ─── helpers ──────────────────────────────────────────────────────── */
const PRIORITY_LABEL = {
  critical:   "Critical Attention",
  elevated:   "Elevated Attention",
  monitoring: "Monitoring",
};

const priorityTone = (p) =>
  attentionToneFor(
    p === "critical"   ? "Critical"   :
    p === "elevated"   ? "Elevated"   :
    p === "monitoring" ? "Monitoring" : "Stable",
  );

const ESCALATION_TONE = {
  overdue:   { bg: "rgba(244,63,94,0.10)",   fg: "#9F1239" },
  imminent:  { bg: "rgba(245,158,11,0.12)",  fg: "#92400E" },
  scheduled: { bg: "rgba(148,163,184,0.10)", fg: "#475569" },
};

const escalationCopy = (state) =>
  state === "overdue"   ? "Escalation overdue" :
  state === "imminent"  ? "Escalation imminent" :
  "Escalation scheduled";

const ROLE_LABEL = {
  buyer:            "Buyer",
  seller:           "Seller",
  buyer_solicitor:  "Buyer solicitor",
  seller_solicitor: "Seller solicitor",
  mortgage_broker:  "Mortgage broker",
  negotiator:       "Negotiator",
  director:         "Director",
};

const roleLabel = (r) => ROLE_LABEL[r] || (r || "").replace(/_/g, " ");

/* ─── sub-components ───────────────────────────────────────────────── */

const PriorityPill = ({ priority }) => {
  const tone = priorityTone(priority);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full
                 text-[10px] font-bold uppercase tracking-wider"
      style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
      data-testid="playbook-priority"
    >
      <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: tone.dot }} />
      {PRIORITY_LABEL[priority] || "Operational"}
    </span>
  );
};

const SectionLabel = ({ icon: Icon, children, testId }) => (
  <div className="flex items-center gap-1.5 mt-3 mb-1.5" data-testid={testId}>
    {Icon ? (
      <Icon size={11} strokeWidth={2.3} style={{ color: "var(--gp-violet, #7C3AED)" }} />
    ) : null}
    <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
       style={{ color: "var(--gp-violet, #7C3AED)" }}>
      {children}
    </p>
  </div>
);

const ImpactRow = ({ impact }) => {
  if (!impact) return null;
  return (
    <div className="flex items-start gap-2.5 mt-1.5">
      <Sparkles size={12} className="shrink-0 mt-0.5 text-violet-500" strokeWidth={2.2} />
      <div className="min-w-0">
        <p className="text-[12px] leading-snug text-slate-800" data-testid="playbook-completion-impact">
          {impact.narrative}
        </p>
        {impact.context_narrative ? (
          <p className="text-[11px] mt-0.5 text-slate-500">{impact.context_narrative}</p>
        ) : null}
      </div>
    </div>
  );
};

const ChainImpactRow = ({ chain }) => {
  if (!chain) return null;
  const tone =
    chain.band === "fragile"  ? attentionToneFor("Critical") :
    chain.band === "watching" ? attentionToneFor("Monitoring") :
    attentionToneFor("Stable");
  return (
    <div className="flex items-start gap-2.5 mt-1.5">
      <GitBranch size={12} className="shrink-0 mt-0.5" style={{ color: tone.dot }} strokeWidth={2.2} />
      <div className="min-w-0">
        <p className="text-[12px] leading-snug text-slate-800" data-testid="playbook-chain-impact">
          {chain.narrative}
        </p>
        {chain.weakest_link && chain.weakest_link.score ? (
          <p className="text-[11px] mt-0.5 text-slate-500"
             data-testid="playbook-chain-weakest-link">
            Weakest link: {chain.weakest_link.name}
            {chain.weakest_link.score ? ` · ${chain.weakest_link.score}/100` : ""}
            {chain.weakest_link.position ? ` · ${chain.weakest_link.position}` : ""}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const ConfidenceImpactRow = ({ confidence }) => {
  if (!confidence) return null;
  const ex = confidence.exchange;
  const co = confidence.completion;
  if (!ex && !co && !confidence.estimated_recovery_points) return null;
  const Icon =
    confidence.band_movement === "weakening" ? TrendingDown :
    confidence.band_movement === "improving" ? TrendingUp : Gauge;
  // Compose calm delta chip when previous_score + delta are present.
  const renderDelta = (m) => {
    if (!m || m.delta === undefined || m.previous_score === undefined) return null;
    const dir = m.delta < 0 ? "↓" : m.delta > 0 ? "↑" : "→";
    return (
      <span className="ml-1 tabular-nums" style={{ opacity: 0.8 }}>
        · {m.previous_score} {dir} {m.score}
      </span>
    );
  };
  return (
    <div className="flex items-start gap-2.5 mt-1.5"
         data-testid="playbook-confidence-impact">
      <Icon size={12} className="shrink-0 mt-0.5 text-violet-500" strokeWidth={2.2} />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] leading-snug text-slate-800">
          {confidence.narrative}
        </p>
        {(ex || co) ? (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {ex ? (
              <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                    style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}
                    data-testid="playbook-confidence-exchange">
                Exchange · {ex.band} · {ex.score}/100
                {renderDelta(ex)}
              </span>
            ) : null}
            {co ? (
              <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                    style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}
                    data-testid="playbook-confidence-completion">
                Completion · {co.band} · {co.score}/100
                {renderDelta(co)}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const OperationalNotesBlock = ({ notes }) => {
  if (!notes || !notes.narrative) return null;
  return (
    <div className="rounded-lg p-3 mt-2"
         style={{
           background: "rgba(124,58,237,0.04)",
           border: "1px solid rgba(124,58,237,0.10)",
         }}
         data-testid="playbook-operational-notes">
      <p className="text-[11.5px] leading-snug text-slate-800">
        {notes.narrative}
      </p>
      {notes.evidence && notes.evidence.length ? (
        <div className="flex flex-wrap gap-1.5 mt-2"
             data-testid="playbook-operational-notes-evidence">
          {notes.evidence.map((e, idx) => (
            <span key={idx}
                  className="text-[10.5px] px-2 py-0.5 rounded-md"
                  style={{
                    background: "rgba(255,255,255,0.7)",
                    color: "#475569",
                    border: "1px solid rgba(148,163,184,0.20)",
                  }}>
              <span className="font-semibold">{e.label}</span> · {e.value}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const ParticipantsStrip = ({ list }) => {
  if (!list || !list.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="playbook-participants">
      {list.map((p, idx) => (
        <span
          key={`${p.role}-${idx}`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px]"
          style={{
            background: "rgba(99,102,241,0.06)",
            color: "#4338CA",
            border: "1px solid rgba(99,102,241,0.16)",
          }}
        >
          <Users size={10} strokeWidth={2.2} />
          <span className="font-semibold">{roleLabel(p.role)}</span>
          {p.name && p.name !== "—" ? (
            <span className="text-slate-600">· {p.name}</span>
          ) : null}
        </span>
      ))}
    </div>
  );
};

const EscalationRow = ({ state, label }) => {
  const tone = ESCALATION_TONE[state] || ESCALATION_TONE.scheduled;
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md
                    text-[10.5px] font-semibold"
         style={{ background: tone.bg, color: tone.fg,
                  border: `1px solid ${tone.fg}22` }}
         data-testid="playbook-escalation">
      <Clock size={10} strokeWidth={2.3} />
      {escalationCopy(state)} · {label}
    </div>
  );
};

const HistoryRow = ({ history }) => {
  if (!history) return null;
  return (
    <div className="flex items-start gap-2.5 mt-1.5">
      <History size={12} className="shrink-0 mt-0.5 text-slate-400" strokeWidth={2.2} />
      <p className="text-[11.5px] leading-snug text-slate-700"
         data-testid="playbook-historical-resolution">
        {history.narrative}
      </p>
    </div>
  );
};

const ActionRow = ({ action, onComplete, busy, readOnly }) => {
  const completed = !!action.completed_at;
  const escTone = ESCALATION_TONE[action.escalation_state] || ESCALATION_TONE.scheduled;
  return (
    <li className="flex items-start gap-3 py-2.5"
        data-testid={`playbook-action-${action.step_id}`}>
      <button
        type="button"
        onClick={onComplete}
        disabled={completed || busy || readOnly}
        aria-label={completed ? "Step complete" : "Mark complete"}
        className="shrink-0 mt-0.5 w-6 h-6 rounded-md inline-flex items-center justify-center transition"
        style={{
          background: completed ? "rgba(16,185,129,0.14)" : "rgba(255,255,255,0.7)",
          border: completed ? "1px solid rgba(16,185,129,0.40)" : "1px solid var(--gp-card-border)",
          cursor: completed || readOnly ? "default" : "pointer",
        }}
        data-testid={`playbook-tick-${action.step_id}`}
      >
        {completed ? (
          <CheckCircle2 size={14} strokeWidth={2.5} style={{ color: "#047857" }} />
        ) : null}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-bold text-violet-700">
            {action.verb}
          </span>
          <p className={`text-[12.5px] leading-snug ${completed ? "line-through text-slate-400" : "text-slate-900"}`}>
            {action.summary}
          </p>
        </div>
        <p className="text-[11px] mt-0.5 text-slate-500 leading-snug">
          {action.rationale}
        </p>
        <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
          <span className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: "rgba(148,163,184,0.10)", color: "#475569" }}>
            {roleLabel(action.target_role)}
            {action.target_name && action.target_name !== "—"
              ? ` · ${action.target_name}` : ""}
          </span>
          {!completed ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                  style={{ background: escTone.bg, color: escTone.fg }}>
              {action.deadline_label}
            </span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: "rgba(16,185,129,0.08)", color: "#047857" }}>
              Completed {fmtRelativeTime(action.completed_at)}
              {action.completed_by ? ` · ${action.completed_by}` : ""}
            </span>
          )}
        </div>
      </div>
    </li>
  );
};

/* ─── PlaybookCard ─────────────────────────────────────────────────── */

export function PlaybookCard({
  playbook,
  defaultOpen = true,
  onCompleteStep,
  onReviewDeal,
  readOnly = false,
  compact = false,
  testIdSuffix,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [busyStep, setBusyStep] = useState(null);
  const tone = useMemo(() => priorityTone(playbook.priority), [playbook.priority]);

  const handleTick = async (stepId) => {
    if (!onCompleteStep || busyStep || readOnly) return;
    setBusyStep(stepId);
    try { await onCompleteStep(stepId); } finally { setBusyStep(null); }
  };

  const suffix = testIdSuffix || playbook.run_id || playbook.trigger_kind;

  return (
    <article
      className="gp-card overflow-hidden"
      style={{ borderLeft: `3px solid ${tone.dot}` }}
      data-testid={`playbook-card-${suffix}`}
    >
      {/* Header */}
      <header className="p-4 sm:p-5 flex items-start gap-3">
        <div
          className="shrink-0 w-9 h-9 rounded-lg inline-flex items-center justify-center"
          style={{
            background: tone.bg, color: tone.fg,
            border: `1px solid ${tone.border}`,
          }}
        >
          <ClipboardList size={15} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-violet-700">
                Operational Playbook
              </p>
              <h3 className="text-[14.5px] font-semibold text-slate-900 mt-0.5 leading-tight"
                  data-testid={`playbook-title-${suffix}`}>
                {playbook.title}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <PriorityPill priority={playbook.priority} />
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="p-1 rounded-md hover:bg-slate-100"
                aria-label={open ? "Collapse playbook" : "Expand playbook"}
                data-testid={`playbook-toggle-${suffix}`}
              >
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>
          <p className="text-[12px] mt-1.5 leading-snug text-slate-700"
             data-testid={`playbook-situation-${suffix}`}>
            {playbook.situation}
          </p>

          {/* Progress + escalation summary */}
          <div className="flex items-center flex-wrap gap-2 mt-2.5">
            <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-md"
                  style={{ background: "rgba(99,102,241,0.08)", color: "#4338CA" }}
                  data-testid={`playbook-progress-${suffix}`}>
              {playbook.progress?.label || "—"}
            </span>
            {playbook.escalation_state ? (
              <EscalationRow
                state={playbook.escalation_state.state}
                label={playbook.escalation_state.deadline_label}
              />
            ) : null}
            {playbook.revenue_exposure_gbp ? (
              <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-md inline-flex items-center gap-1"
                    style={{
                      background: "rgba(245,158,11,0.10)",
                      color: "#92400E",
                      border: "1px solid rgba(245,158,11,0.22)",
                    }}
                    data-testid={`playbook-fee-exposure-${suffix}`}>
                £{Number(playbook.revenue_exposure_gbp).toLocaleString()} fee exposure
              </span>
            ) : null}
            {onReviewDeal && playbook.deal_id ? (
              <button
                type="button"
                onClick={() => onReviewDeal(playbook.deal_id)}
                className="text-[10.5px] font-semibold inline-flex items-center gap-1
                           text-violet-700 hover:underline"
                data-testid={`playbook-review-deal-${suffix}`}
              >
                Open deal room
                <ArrowUpRight size={11} />
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {/* Body */}
      {open ? (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5"
             style={{ borderTop: "1px solid var(--gp-card-border)" }}>
          {/* Impact lines */}
          <SectionLabel icon={Sparkles} testId={`playbook-impact-section-${suffix}`}>
            Completion impact
          </SectionLabel>
          <ImpactRow impact={playbook.completion_impact} />
          <ChainImpactRow chain={playbook.chain_impact} />

          {/* Confidence impact (Phase 22) */}
          {playbook.confidence_impact ? (
            <>
              <SectionLabel icon={Gauge} testId={`playbook-confidence-section-${suffix}`}>
                Confidence impact
              </SectionLabel>
              <ConfidenceImpactRow confidence={playbook.confidence_impact} />
            </>
          ) : null}

          {/* Operational notes (Phase 22) */}
          {playbook.operational_notes && playbook.operational_notes.narrative ? (
            <>
              <SectionLabel icon={BookOpen} testId={`playbook-notes-section-${suffix}`}>
                Operational notes
              </SectionLabel>
              <OperationalNotesBlock notes={playbook.operational_notes} />
            </>
          ) : null}

          {/* Participants */}
          {playbook.impacted_participants?.length ? (
            <>
              <SectionLabel icon={Users} testId={`playbook-participants-section-${suffix}`}>
                Impacted participants
              </SectionLabel>
              <ParticipantsStrip list={playbook.impacted_participants} />
            </>
          ) : null}

          {/* Recommended actions */}
          <SectionLabel icon={ClipboardList} testId={`playbook-actions-section-${suffix}`}>
            Recommended operational sequence
          </SectionLabel>
          <ul className="divide-y" style={{ borderColor: "var(--gp-card-border)" }}>
            {playbook.recommended_actions?.map((a) => (
              <ActionRow
                key={a.step_id}
                action={a}
                busy={busyStep === a.step_id}
                readOnly={readOnly}
                onComplete={() => handleTick(a.step_id)}
              />
            ))}
          </ul>

          {/* History */}
          {playbook.historical_resolution ? (
            <>
              <SectionLabel icon={History} testId={`playbook-history-section-${suffix}`}>
                Historical resolution pattern
              </SectionLabel>
              <HistoryRow history={playbook.historical_resolution} />
            </>
          ) : null}

          {/* Audit trail (compact list of last 3) */}
          {!compact && playbook.audit_trail?.length ? (
            <>
              <SectionLabel icon={Layers3} testId={`playbook-audit-section-${suffix}`}>
                Audit trail
              </SectionLabel>
              <ul className="space-y-1 mt-1">
                {playbook.audit_trail.slice(-4).map((row, idx) => (
                  <li key={idx} className="flex items-baseline gap-2 text-[11px] text-slate-600">
                    <span className="inline-block w-1 h-1 rounded-full mt-1.5 bg-slate-300" />
                    <span className="font-semibold text-slate-800">{row.label}</span>
                    <span className="text-slate-400">
                      · {row.by} · {fmtRelativeTime(row.at)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default PlaybookCard;
